/**
 * Demo content the documentation's live embeds and element previews render.
 * These are credentials and resource identifiers rather than infrastructure,
 * which is why they sit beside `endpoints.ts` rather than inside it: that module
 * is where the documentation points, this one is what it points at. Each value
 * reads a Vite build-time override (`VITE_*`) so a preview deploy renders against
 * its own demo organization without code changes, while a plain `vite build`
 * ships the public demo content.
 */

/**
 * DEMO_PUBLISHABLE_KEY is the publishable key a live embed falls back to, so an
 * authored page renders against real data without pinning credentials of its own;
 * an author overrides it per embed. It is scoped to a demo organization holding
 * published public resources, and its origin allowlist names the documentation
 * host. A publishable key ships in the browser by definition, so what contains it
 * is that scope rather than any secrecy about the value.
 */
export const DEMO_PUBLISHABLE_KEY: string =
  import.meta.env.VITE_DEMO_PUBLISHABLE_KEY ?? "pk_d5947e9275121da0ebeacb6de4ca6552";

/**
 * DEMO_ITINERARY_ID is the published itinerary every live surface on this site draws.
 *
 * One itinerary, named once. The element previews resolve it through a baked fixture
 * and the hosted embed's frames resolve it through the hosted studio, so the two paths
 * read different transports and must not read different content — a page that showed a
 * profile of one itinerary beside a map of another would be documenting nothing.
 *
 * It is also what `scripts/refresh-fixtures.mjs` reads, from the same `VITE_DEMO_ITINERARY_ID`
 * override with the same default, so the baked fixture and the live frames cannot drift apart.
 *
 * Every live embed defaults to it. Without one the frame renders a bare basemap: correct,
 * and a poor illustration of a product whose whole subject is an itinerary.
 */
export const DEMO_ITINERARY_ID: string =
  import.meta.env.VITE_DEMO_ITINERARY_ID ?? "bb908a79-dcc1-4db2-aba8-7238dd8f15fc";
